import { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    IconButton,
    Tooltip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import API from "../api";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { Panel } from "../scenes-reseller/dashboard/dashboard-components/resellerUi";
import WarningDialog from "./WarningDialog";

// ─── Allowed A4 resolutions for background ───────────────────────────────────
const A4_ALLOWED = [
    { dpi: 72, w: 595, h: 842 },
    { dpi: 96, w: 794, h: 1123 },
    { dpi: 150, w: 1240, h: 1754 },
    { dpi: 300, w: 2480, h: 3508 },
    { dpi: 600, w: 4961, h: 7016 },
];

function isA4Resolution(width, height) {
    return A4_ALLOWED.some((r) => r.w === width && r.h === height);
}

// ─── Image template component ─────────────────────────────────────────────────
function ImageTemplateSection({
    label,
    getFn,
    uploadFn,
    deleteFn,
    maxWidth = Infinity,
    maxHeight = Infinity,
    a4Only = false,
}) {
    const [previewUrl, setPreviewUrl] = useState(null);
    const [loadingGet, setLoadingGet] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [status, setStatus] = useState("");
    const [isError, setIsError] = useState(false);
    const [fileName, setFileName] = useState("");

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const fileRef = useRef(null);
    const objectUrlRef = useRef(null);

    const updateStatus = (msg, error = false) => {
        setStatus(msg);
        setIsError(error);
    };

    const revokeUrl = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    };

    const fetchImage = () => {
        setLoadingGet(true);
        updateStatus("");

        getFn()
            .then((response) => {
                revokeUrl();
                const url = URL.createObjectURL(response.data);
                objectUrlRef.current = url;
                setPreviewUrl(url);
                updateStatus("");
            })
            .catch((error) => {
                if (error.response?.status === 404) {
                    setPreviewUrl(null);
                    updateStatus("No image set.", false);
                } else {
                    updateStatus("Error loading image.", true);
                }
            })
            .finally(() => setLoadingGet(false));
    };

    useEffect(() => {
        fetchImage();

        return () => revokeUrl();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFileChange = (e) => {
        const file = e?.target?.files?.[0];
        if (!file) return;

        setFileName(file.name);

        const tempUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            URL.revokeObjectURL(tempUrl);

            if (a4Only) {
                if (!isA4Resolution(img.width, img.height)) {
                    updateStatus(
                        `Dimensions not allowed: ${img.width}×${img.height}px. Use one of the A4 resolutions shown (hover over the ℹ icon).`,
                        true
                    );
                    if (fileRef.current) fileRef.current.value = "";
                    setFileName("");
                    return;
                }
            }

            if (img.width > maxWidth || img.height > maxHeight) {
                updateStatus(
                    `Image too large: ${img.width}×${img.height}px. Maximum allowed: ${maxWidth}×${maxHeight}px.`,
                    true
                );
                if (fileRef.current) fileRef.current.value = "";
                setFileName("");
                return;
            }

            const formData = new FormData();
            formData.append("file", file, file.name);

            setUploading(true);
            updateStatus("Uploading…");

            uploadFn(formData)
                .then(() => {
                    updateStatus("Image uploaded successfully.", false);
                    if (fileRef.current) fileRef.current.value = "";
                    setFileName("");
                    fetchImage();
                })
                .catch((error) => {
                    const detail = error.response?.data?.detail || error.message;
                    updateStatus(`Error: ${detail}`, true);
                })
                .finally(() => setUploading(false));
        };

        img.onerror = () => {
            URL.revokeObjectURL(tempUrl);
            updateStatus("Invalid or unreadable file.", true);
            if (fileRef.current) fileRef.current.value = "";
            setFileName("");
        };

        img.src = tempUrl;
    };

    const handleDelete = () => {
        setOpenDeleteDialog(true);
    };

    useEffect(() => {
        if (!confirmDelete) return;

        setOpenDeleteDialog(false);
        setConfirmDelete(false);

        setDeleting(true);
        updateStatus("Deleting…");

        deleteFn()
            .then(() => {
                revokeUrl();
                setPreviewUrl(null);
                updateStatus("Image deleted.", false);
            })
            .catch((error) => {
                const detail = error.response?.data?.detail || error.message;
                updateStatus(`Error: ${detail}`, true);
            })
            .finally(() => setDeleting(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [confirmDelete]);

    const hasLimit = maxWidth !== Infinity || maxHeight !== Infinity;
    const busy = uploading || deleting;

    return (
        <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
                <Typography variant="subtitle2">{label}</Typography>

                {a4Only && (
                    <Tooltip
                        title={
                            <Box>
                                <Typography variant="caption" fontWeight={700} display="block" mb={0.5}>
                                    Allowed A4 resolutions:
                                </Typography>
                                {A4_ALLOWED.map(({ dpi, w, h }) => (
                                    <Typography key={dpi} variant="caption" display="block">
                                        {dpi} DPI — {w} × {h} px
                                    </Typography>
                                ))}
                            </Box>
                        }
                        arrow
                        placement="right"
                    >
                        <InfoOutlinedIcon
                            fontSize="small"
                            sx={{ color: "text.secondary", cursor: "help", fontSize: 16 }}
                        />
                    </Tooltip>
                )}

                {hasLimit && !a4Only && (
                    <Typography variant="caption" color="text.secondary">
                        (max {maxWidth}×{maxHeight}px)
                    </Typography>
                )}
            </Box>

            <Box
                sx={{
                    width: "100%",
                    height: 110,
                    border: "1px dashed",
                    borderColor: "divider",
                    borderRadius: 1,
                    mb: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: "rgba(0,0,0,0.2)",
                }}
            >
                {loadingGet || uploading ? (
                    <CircularProgress size={24} />
                ) : previewUrl ? (
                    <img
                        src={previewUrl}
                        alt={label}
                        style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                    />
                ) : (
                    <Typography variant="caption" color="text.secondary">
                        No image set
                    </Typography>
                )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/svg+xml,image/webp"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    disabled={busy}
                />

                <Box
                    component="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={busy}
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "5px",
                        px: "10px",
                        py: "4px",
                        fontSize: 12,
                        fontWeight: 500,
                        fontFamily: "inherit",
                        border: "0.5px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        color: "text.secondary",
                        cursor: busy ? "not-allowed" : "pointer",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        transition: "background 0.15s, color 0.15s",
                        "&:hover:not(:disabled)": {
                            bgcolor: "action.hover",
                            color: "text.primary",
                        },
                        "&:active:not(:disabled)": {
                            transform: "scale(0.97)",
                        },
                    }}
                >
                    Choose file
                </Box>

                <Typography
                    variant="caption"
                    color="text.disabled"
                    noWrap
                    sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", fontSize: 12 }}
                >
                    {fileName || "No file chosen"}
                </Typography>

                <Tooltip title="Delete">
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleDelete}
                            disabled={busy || !previewUrl}
                            sx={{
                                width: 24,
                                height: 24,
                                border: "0.5px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                                color: "text.disabled",
                                "&:hover": {
                                    bgcolor: "error.light",
                                    color: "error.main",
                                    borderColor: "error.light",
                                },
                            }}
                        >
                            {deleting ? (
                                <CircularProgress size={14} />
                            ) : (
                                <DeleteIcon sx={{ fontSize: 14 }} />
                            )}
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Box sx={{ mt: 0.5, height: "3em", overflow: "hidden" }}>
                <Typography
                    variant="caption"
                    sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        color: isError ? "error.main" : "success.main",
                        fontFamily: "monospace",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        whiteSpace: "normal",
                        visibility: status ? "visible" : "hidden",
                    }}
                >
                    {status || " "}
                </Typography>
            </Box>

            <WarningDialog
                open={openDeleteDialog}
                setOpen={setOpenDeleteDialog}
                setCheck={setConfirmDelete}
                message={`Remove the image set for "${label}"?`}
            />
        </Box>
    );
}

// Riquadro "Image templates" (Background + Logo) condiviso tra le pagine di report.
const ImageTemplates = () => (
    <Panel icon={ImageOutlinedIcon} title="Image templates" sx={{ height: "auto", minHeight: 0 }}>
        <Box sx={{ p: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
            <Box
                sx={{
                    flex: "0 0 calc(50% - 8px)",
                    width: "calc(50% - 8px)",
                    maxWidth: "calc(50% - 8px)",
                    minWidth: 0,
                    overflow: "hidden",
                }}
            >
                <ImageTemplateSection
                    label="Background"
                    getFn={API.getBackgroundTemplate}
                    uploadFn={API.uploadBackgroundTemplate}
                    deleteFn={API.deleteBackgroundTemplate}
                    a4Only
                />
            </Box>

            <Box
                sx={{
                    flex: "0 0 calc(50% - 8px)",
                    width: "calc(50% - 8px)",
                    maxWidth: "calc(50% - 8px)",
                    minWidth: 0,
                    overflow: "hidden",
                }}
            >
                <ImageTemplateSection
                    label="Logo"
                    getFn={API.getLogoTemplate}
                    uploadFn={API.uploadLogoTemplate}
                    deleteFn={API.deleteLogoTemplate}
                    maxWidth={600}
                    maxHeight={400}
                />
            </Box>
        </Box>
        </Box>
    </Panel>
);

export default ImageTemplates;
